'use client'

import { GeneralModal, IconBox } from '@/components/ui/general-modal'
import { AdGrowthChart } from './ad-growth-chart'
import { TrendingUp, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/utils/styles/cn'
import type { IResearchedAd } from '@/api/research-jobs/ads/get-researched-ads-query'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { IGrowthTimeframeFilterValue } from '@/features/research/research-ads-filters/state/research-ads-filters-state-types'
import { useGetAdTrendDataQuery } from '@/api/research-jobs/ads/get-ad-trend-data-query'
import { SceneGenerationStepResultCardButton } from '@/features/generations/generation-result/scene-generation-step-result-card-button'

interface GrowthIndicatorProps {
    adUuid: string
    trendMetrics?: NonNullable<IResearchedAd['metrics']['trendMetrics']> | null
    timeframe?: IGrowthTimeframeFilterValue
    /**
     * `overlay` renders the card's hover glass icon button (needs `hidden` from the overlay);
     * `modal-only` renders no trigger at all — the host opens the chart via the controlled props.
     */
    variant?: 'default' | 'button' | 'overlay' | 'modal-only'
    /** Overlay variant only: fades the glass button with the rest of the hover actions. */
    hidden?: boolean
    /** Controlled chart-open state, for hosts that trigger the chart from their own UI. */
    open?: boolean
    onOpenChange?: (open: boolean) => void
    /**
     * Default variant only. When false the growth number renders as a static, non-clickable value:
     * the chart modal it opens fetches org-scoped trend data, which is a dead end on read-only
     * surfaces (the anonymous public board has no org, so the query never runs and the modal spins
     * forever). Defaults to interactive.
     */
    interactive?: boolean
    className?: string
}

export function GrowthIndicator({
    adUuid,
    trendMetrics,
    variant = 'default',
    hidden,
    open,
    onOpenChange,
    interactive = true,
    className,
}: GrowthIndicatorProps) {
    const [internalChartOpen, setInternalChartOpen] = useState(false)
    const isChartOpen = open ?? internalChartOpen
    const setIsChartOpen = onOpenChange ?? setInternalChartOpen

    const { data: trendData, isLoading: isTrendDataLoading } = useGetAdTrendDataQuery(adUuid, isChartOpen)

    const hasGrowthData = !!trendMetrics?.growth

    const growthValue = trendMetrics?.growth['7d']

    const chartData =
        trendData?.rawData && trendMetrics?.growth ? { growth: trendMetrics.growth, rawData: trendData.rawData } : null

    const renderButton = () => {
        return (
            <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                    <div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                if (hasGrowthData) {
                                    setIsChartOpen(true)
                                }
                            }}
                            disabled={!hasGrowthData}
                            className={cn('w-full flex items-center gap-1', className)}
                        >
                            <TrendingUp className="h-4 w-4" />
                            <span>View Growth over Time</span>
                        </Button>
                    </div>
                </TooltipTrigger>
                {!hasGrowthData && (
                    <TooltipContent>
                        <p>We are still collecting data for this timeframe</p>
                    </TooltipContent>
                )}
            </Tooltip>
        )
    }

    const renderDefaultVariant = () => {
        if (growthValue === null || growthValue === undefined) {
            return null
        }

        const clickable = interactive && hasGrowthData

        return (
            <div
                onClick={() => clickable && setIsChartOpen(true)}
                className={cn(
                    'inline-flex items-center gap-1 text-[0.6rem] text-green-500 h-[20px]',
                    clickable && 'cursor-pointer hover:opacity-80 transition-opacity',
                    className
                )}
            >
                <span className="inline-flex items-center h-[20px]">
                    {`${growthValue >= 0 ? '+' : ''}${growthValue.toFixed(1)}%`}
                </span>
            </div>
        )
    }

    const renderChart = () => {
        return (
            <GeneralModal
                open={isChartOpen}
                onOpenChange={setIsChartOpen}
                title="Growth Chart"
                subtitle="View ad performance trends over time"
                icon={
                    <IconBox>
                        <TrendingUp className="h-5 w-5 text-[#6F6F6F]" />
                    </IconBox>
                }
                className="md:w-6xl"
            >
                {isTrendDataLoading || !chartData ? (
                    <div className="flex items-center justify-center h-[400px]">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <AdGrowthChart data={chartData} className="w-full" />
                )}
            </GeneralModal>
        )
    }

    const renderOverlayButton = () => {
        return (
            <SceneGenerationStepResultCardButton
                hidden={Boolean(hidden)}
                Icon={TrendingUp}
                disabled={!hasGrowthData}
                tooltip={hasGrowthData ? 'View growth over time' : 'We are still collecting data for this timeframe'}
                onClick={() => setIsChartOpen(true)}
            />
        )
    }

    return (
        <>
            {variant === 'button'
                ? renderButton()
                : variant === 'overlay'
                  ? renderOverlayButton()
                  : variant === 'modal-only'
                    ? null
                    : renderDefaultVariant()}
            {renderChart()}
        </>
    )
}
